// Helper functions for course data manipulation
import store from '../store/store';
import { selectDisplayedCourse } from './course.translation';

/**
 * Helper function to find parent by ID pattern
 * @param courseData - The full course data structure
 * @param childId - The child ID (e.g., "1.1.1")
 * @returns The parent object or null if not found
 */
export const findParentById = (courseData: any, childId: string) => {
    if (!courseData || !childId) return null;
    
    // Extract parent ID (e.g., "1.1.1" -> "1.1")
    const idParts = childId.split('.');
    if (idParts.length <= 1) return null;
    
    const parentId = idParts.slice(0, -1).join('.');
    
    // Recursive function to search through course structure
    const searchForItem = (items: any[]) => {
        if (!Array.isArray(items)) return null;
        
        for (const item of items) {
            if (item.id === parentId) {
                return item;
            }
            if (item.children) {
                const found = searchForItem(item.children);
                if (found) return found;
            }
        }
        return null;
    };
    
    // Start search from course root
    if (courseData.children) {
        return searchForItem(courseData.children);
    }
    
    return null;
};

/**
 * Helper function to clean up module titles
 * @param title - The title string to clean
 * @returns Cleaned title without "Module X -" prefix
 */
export const cleanModuleTitle = (title: string) => {
    if (title) {
        return title.replace(/^(?:Module|Módulo)\s+\d+\s*[-–—]\s*/i, '').trim();
    }
    return title;
};

/**
 * Get course data from Redux store
 * @returns Course data from store or null
 */
const getCourseDataFromStore = () => {
    try {
        const state = store.getState();
        return selectDisplayedCourse(state) || null;
    } catch (error) {
        return null;
    }
};

/**
 * Get display title for course items, preferring parent module title for videos
 * @param selected - The selected course item
 * @returns Display title string
 */
export const getDisplayTitle = (selected: any) => {
    // For videos, try to get parent module title from store
    if (selected.type === 'video') {
        const courseData = getCourseDataFromStore();
        if (courseData) {
            const parent = findParentById(courseData, selected.id);
            if (parent && parent.title) {
                return cleanModuleTitle(parent.title);
            }
        }
    }
    
    return selected.title || selected.name || 'Content';
};

/**
 * Find the related video for a quiz (within the same module)
 * @param quizId - The quiz ID
 * @returns The related video object or null
 */
export const findRelatedVideoForQuiz = (quizId: string): any => {
    const courseData = getCourseDataFromStore();
    if (!courseData) return null;
    
    // Find the quiz's parent module
    const parentModule = findParentById(courseData, quizId);
    if (!parentModule || !parentModule.children) return null;
    
    // Find the video within the same module
    const video = parentModule.children.find((child: any) => child.type === 'video');
    return video || null;
};
